import { NavLink, Outlet, useOutletContext, useParams } from 'react-router-dom'
import { statusNote, type ProjectStatus } from '../lib/project-status.js'
import { useApi, useAsync } from '../lib/useApi.js'
import { ErrorBox, Loading, StatusPill } from './ui.js'
import type { Project } from '../lib/api.js'

export const PROJECT_SECTIONS = [
  ['', 'Overview', '◫'],
  ['database', 'Database', '▤'],
  ['deployments', 'Deployments', '↗'],
  // Next to the database, because both answer "who can reach my data".
  ['storage', 'Storage', '⬚'],
  // With the other places data lives. Search is the only way to see inside
  // an index, so this sits where somebody looks for their content.
  ['vectors', 'Vector search', '◈'],
  // Next to the data it reports on. "Is realtime working?" had no answer
  // anywhere before this page.
  ['realtime', 'Realtime', '◐'],
  ['auth', 'Authentication', '◎'],
  ['domains', 'Domains', '◇'],
  // Before Diagnostics on purpose: "what happened?" is the question people
  // arrive with, and diagnostics is where they go when the log did not
  // answer it.
  ['logs', 'Logs', '≡'],
  ['diagnostics', 'Diagnostics', '⌁'],
  ['keys', 'API keys', '⌘'],
  ['settings', 'Settings', '⚙'],
] as const

export function useProject() {
  return useOutletContext<{ project: Project; reload: () => void }>()
}

export default function ProjectLayout() {
  const { id = '' } = useParams()
  const api = useApi()
  const state = useAsync(() => api.getProject(id), [api, id])
  const project = state.data?.project.id === id ? state.data.project : null
  return (
    <div className="project-workspace">
      <aside className="project-sidebar">
        <NavLink className="back-link" to="/projects">
          ← All projects
        </NavLink>
        <div className="project-identity">
          <span className="project-avatar" aria-hidden="true">
            {project?.name.charAt(0).toUpperCase() ?? 'N'}
          </span>
          <div>
            <strong>{project?.name ?? 'Project'}</strong>
            <span className="sub small">Project workspace</span>
          </div>
        </div>
        <nav aria-label="Project navigation">
          {PROJECT_SECTIONS.map(([path, label, icon]) => (
            <NavLink key={path} end={!path} to={`/projects/${id}${path ? `/${path}` : ''}`}>
              <span aria-hidden="true">{icon}</span>
              {label}
            </NavLink>
          ))}
        </nav>
        <p className="sidebar-note">
          Your data. Your project.
          <br />
          One workspace for your backend.
        </p>
      </aside>
      <div className="workspace-body" id="workspace-content" tabIndex={-1}>
        <div className="workspace-context">
          <span>
            {project?.name ?? 'Project'} <span aria-hidden="true">/</span>{' '}
            <span className="muted">Workspace</span>
          </span>
          {project && <StatusPill status={project.status} />}
        </div>
        {project && (() => {
          // Said once, above everything, because every page below it is
          // about a project that may not be answering.
          const note = statusNote(project.status as ProjectStatus)
          return note && (
            <div
              className={note.tone === 'warn' ? 'error' : 'card panel'}
              style={{ marginBottom: 20 }}
            >
              <div>
                <strong>{note.title}</strong>
                <p className="sub small" style={{ margin: '4px 0 0' }}>{note.body}</p>
              </div>
            </div>
          )
        })()}
        {state.loading ? (
          <Loading />
        ) : state.error ? (
          <ErrorBox retry={state.reload}>{state.error}</ErrorBox>
        ) : project ? (
          <Outlet key={id} context={{ project, reload: state.reload }} />
        ) : (
          <ErrorBox retry={state.reload}>Project not found.</ErrorBox>
        )}
      </div>
    </div>
  )
}
